<template>
    <div>
        <!-- List of Attachments -->
        <div class="d-flex flex-wrap align-items-center">
            <draggable
                v-model="attachments"
                v-bind="dragOptions"
                :disabled="!editingEnabled"
                class="d-flex flex-wrap align-items-start"
                tag="ul"
            >
                <div
                    v-for="(attachment, attachmentIndex) in attachments"
                    :key="attachmentIndex"
                    class="attachment-container draggable-item"
                    :class="{ 'attachment-container-readonly': !editingEnabled }"
                >
                    <a
                        :href="attachment.stored_file.signed_url"
                        rel="noopener"
                        target="_blank"
                        class="bg-white d-flex align-items-center rounded-lg border rounded mr-2 attachment"
                        :class="{ 'attachment-editable': editingEnabled }"
                    >
                        <span class="px-1">
                            <span class="align-middle icon-100 icon-glyphicons-basic-paperclip mr-1"></span>
                            <span class="align-middle">{{ attachment.label }}</span>
                        </span>
                    </a>
                    <div v-if="editingEnabled" class="attachment-edit-btns d-flex align-items-center">
                        <button
                            v-if="editingEnabled"
                            @click="openEditAttachment(attachment, attachmentIndex)"
                            class="btn btn-sm btn-transparent mr-1"
                        >
                            <span class="icon icon-100 icon-glyphicons-basic-square-edit align-middle mr-1"></span>
                            <span class="align-middle">Edit</span>
                        </button>
                        <button
                            v-if="editingEnabled"
                            @click="openDeleteAttachment(attachment, attachmentIndex)"
                            class="btn btn-sm btn-transparent"
                        >
                            <span class="icon icon-100 icon-glyphicons-basic-bin align-middle"></span>
                        </button>
                    </div>
                </div>
                <info-popover
                    v-show="editingEnabled"
                    slot="footer"
                    content="Click to add an attachment"
                    :dismiss-on-click="true"
                    placement="top"
                >
                    <div
                        @click="openCreateAttachment()"
                        class="rounded-lg placeholder medium mb-3 h-100 px-3 attachment"
                        :class="{ 'ml-1': attachments.length > 0 }"
                        role="button"
                    >
                        <span class="icon icon-100 icon-glyphicons-basic-plus icon-mr align-middle"></span>
                        <span class="align-middle">Attachment</span>
                    </div>
                </info-popover>
            </draggable>
        </div>
        <!-- Add/Edit Modal -->
        <base-modal ref="editmodal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-100 icon-glyphicons-basic-paperclip align-middle mr-1"></span>
                <span class="align-middle">{{ attachmentModalLabel }} Attachment</span>
            </template>
            <template>
                <p>Add an attachment for page viewers to download.</p>
                <div v-if="selectedAttachment">
                    <file-upload
                        v-model="selectedAttachment.stored_file"
                        :max-bytes="30000000"
                        :direct-upload="true"
                        placeholder="Drop a file here"
                        accept="All file types accepted"
                        ref="attachmentUpload"
                    ></file-upload>
                    <base-form :fields="attachmentFields" v-model="selectedAttachment" ref="attachmentForm"></base-form>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    class="btn btn-primary"
                    type="button"
                    :disabled="modalLoading || noAttachmentAdded"
                    @click="saveSelectedAttachment()"
                >
                    {{ attachmentModalLabel }}
                </button>
            </template>
        </base-modal>
        <!-- Remove Modal -->
        <base-modal ref="removemodal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-100 icon-glyphicons-basic-bin align-middle mr-1"></span>
                <span class="align-middle">Remove Attachment</span>
            </template>
            <template>
                <p class="my-2">
                    Remove attachment <strong>{{ selectedAttachment.label }}</strong
                    >?
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="deleteAttachment()" class="btn btn-danger" type="button">Remove</button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import draggable from "vuedraggable"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import FileUpload from "@/components/FileUpload"
import InfoPopover from "@/components/InfoPopover"

export default {
    name: "PublishPageAttachment",
    components: {
        BaseModal,
        BaseForm,
        FileUpload,
        InfoPopover,
        draggable,
    },
    props: {
        value: {
            type: Array,
            required: false,
            default() {
                return []
            },
        },
        editingEnabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            attachments: this.value,
            modalLoading: false,
            selectedAttachmentIndex: null,
            selectedAttachment: {
                stored_file: null,
                label: "",
            },
            attachmentFields: [
                {
                    name: "Attachment Label",
                    placeholder: "Attachment label",
                    subtext: "Provide a label for this attachment.",
                    type: "text",
                    key: "label",
                    required: true,
                },
            ],
            dragOptions: {
                animation: 300,
                ghostClass: "ghost",
                direction: "horizontal",
                draggable: ".draggable-item",
                dragClass: "sortable-drag",
                chosenClass: "sortable-chosen",
                delay: 500,
                delayOnTouchOnly: true,
            },
        }
    },
    computed: {
        attachmentModalLabel() {
            return this.selectedAttachmentIndex === null ? "Add" : "Update"
        },
        noAttachmentAdded() {
            return !this.selectedAttachment.stored_file
        },
    },
    watch: {
        value(newValue) {
            this.attachments = newValue
        },
        attachments(newValue) {
            this.$emit("input", newValue)
        },
    },
    methods: {
        // Open the attachment modal to create.
        openCreateAttachment() {
            this.selectedAttachment = {
                label: "",
                stored_file: null,
            }
            this.selectedAttachmentIndex = null
            this.$refs.attachmentUpload.reset()
            this.$refs.attachmentForm.resetValidate()
            this.$refs.editmodal.show()
        },
        // Open the attachment modal to edit.
        openEditAttachment(attachment, index) {
            this.selectedAttachment = JSON.parse(JSON.stringify(attachment))
            this.selectedAttachmentIndex = index
            this.$refs.attachmentUpload.reset()
            this.$refs.attachmentForm.resetValidate()
            this.$refs.editmodal.show()
        },
        // Save the selected attachment.
        saveSelectedAttachment() {
            if (!this.$refs.attachmentForm.validate()) {
                return
            }
            if (this.selectedAttachmentIndex === null) {
                this.createAttachment()
            } else {
                this.saveAttachment()
            }
        },
        // Create an attachment.
        createAttachment() {
            this.attachments.push(this.selectedAttachment)
            this.$refs.editmodal.hide()
        },
        // Save the selected attachment.
        saveAttachment() {
            this.attachments.splice(this.selectedAttachmentIndex, 1, this.selectedAttachment)
            this.$refs.editmodal.hide()
        },
        // Open the remove attachment modal.
        openDeleteAttachment(attachment, index) {
            this.selectedAttachment = attachment
            this.selectedAttachmentIndex = index
            this.$refs.removemodal.show()
        },
        // Remove the selected attachment.
        deleteAttachment() {
            this.attachments.splice(this.selectedAttachmentIndex, 1)
            this.$refs.removemodal.hide()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/page.scss";

.attachment-container {
    font-size: 14px;
    font-weight: 500;
    &:hover {
        .attachment-edit-btns {
            opacity: 1;
        }
    }
    .attachment-edit-btns {
        opacity: 0;
        transition: $transition-base;
        margin-top: 1px;
        .btn {
            padding: 1px !important;
        }
    }
    .attachment-editable {
        cursor: grab !important;
    }
}

.attachment {
    padding: 0.4rem;
}

.attachment-container-readonly {
    padding-bottom: 0.7rem;
}
</style>